/**
 * Redacción de PII en telemetría (HU-14, principio 7).
 *
 * El email de un lead no debe llegar a Sentry, al `clientLogger` ni a los logs
 * de consola. Estas rutas transportan datos personales en el cuerpo, así que su
 * request/response se redacta antes de registrarse — sin alterar la respuesta
 * real que consume la app.
 */

/** Rutas cuyo cuerpo contiene PII de lead. */
const SENSITIVE_API_PATHS = [
  /\/leads(?:$|[/?])/,
  /\/admin\/leads(?:$|[/?])/,
  /\/newsletter\/subscribe(?:$|[/?])/,
  /\/admin\/catalog(?:$|[/?])/,
];

const REDACTED = '[redacted]';

const SENSITIVE_KEYS = ['email'];

export const isSensitiveApiPath = (url?: string): boolean =>
  typeof url === 'string' &&
  SENSITIVE_API_PATHS.some((pattern) => pattern.test(url));

/**
 * Clona `value` con los campos sensibles reemplazados por `[redacted]`.
 * Acepta objetos, arrays y strings JSON (que parsea antes de redactar).
 */
export const redactSensitiveFields = (
  value: unknown,
  keys: string[] = SENSITIVE_KEYS
): unknown => {
  if (typeof value === 'string') {
    try {
      return JSON.stringify(redactSensitiveFields(JSON.parse(value), keys));
    } catch {
      return value;
    }
  }

  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveFields(item, keys));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        keys.includes(key.toLowerCase())
          ? REDACTED
          : redactSensitiveFields(item, keys),
      ])
    );
  }

  return value;
};

/** Redacta el payload solo si la ruta es sensible; si no, lo devuelve igual. */
export const redactForApiTelemetry = (
  url: string | undefined,
  payload: unknown
): unknown =>
  isSensitiveApiPath(url) ? redactSensitiveFields(payload) : payload;
